import { Routes, Route, Navigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuthStore } from './stores/authStore'
import { useNotificationStore } from './stores/notificationStore'

// Layouts
import { MainLayout } from './components/layout/MainLayout'
import { AuthLayout } from './components/layout/AuthLayout'

// Pages
import { LoginPage } from './pages/auth/LoginPage'
import { DashboardPage } from './pages/dashboard/DashboardPage'
import { ProductsPage } from './pages/products/ProductsPage'
import { ProductDetailPage } from './pages/products/ProductDetailPage'
import { CreateProductPage } from './pages/products/CreateProductPage'
import { StockPricesPage } from './pages/products/StockPricesPage'
import { BundlesPage } from './pages/bundles/BundlesPage'
import { BundleInstancesPage } from './pages/bundles/BundleInstancesPage'
import { InventoryPage } from './pages/inventory/InventoryPage'
import { InventoryAdjustmentPage } from './pages/inventory/InventoryAdjustmentPage'
import { PurchasesPage } from './pages/purchases/PurchasesPage'
import { PurchaseDetailPage } from './pages/purchases/PurchaseDetailPage'
import { CreatePurchasePage } from './pages/purchases/CreatePurchasePage'
import { ReceivePurchasePage } from './pages/purchases/ReceivePurchasePage'
import { SalesPage } from './pages/sales/SalesPage'
import { SaleDetailPage } from './pages/sales/SaleDetailPage'
import { POSPage } from './pages/sales/POSPage'
import QuotationsListPage from './pages/quotations/QuotationsListPage'
import CreateQuotationPage from './pages/quotations/CreateQuotationPage'
import { CustomersPage } from './pages/customers/CustomersPage'
import { CustomerDetailPage } from './pages/customers/CustomerDetailPage'
import { CreateCustomerPage } from './pages/customers/CreateCustomerPage'
import { CustomerGroupsPage } from './pages/customers/CustomerGroupsPage'
import { ConsignmentsPage } from './pages/consignments/ConsignmentsPage'
import { ConsignmentDetailPage } from './pages/consignments/ConsignmentDetailPage'
import { CreateConsignmentPage } from './pages/consignments/CreateConsignmentPage'
import { PaymentsPage } from './pages/payments/PaymentsPage'
import { InvoicesPage } from './pages/invoices/InvoicesPage'
import { InvoiceDetailPage } from './pages/invoices/InvoiceDetailPage'
import { ReportsPage } from './pages/reports/ReportsPage'
import { DebtorsReportPage } from './pages/reports/DebtorsReportPage'
import { StatementsPage } from './pages/reports/StatementsPage'
import { AIAssistantPage } from './pages/ai/AIAssistantPage'
import AIReportsPage from './pages/ai/AIReportsPage'
import { SettingsPage } from './pages/settings/SettingsPage'
import { BrandsPage } from './pages/settings/BrandsPage'
import { CategoriesPage } from './pages/settings/CategoriesPage'
import { NotificationsPage } from './pages/notifications/NotificationsPage'
import { SuppliersPage } from './pages/suppliers/SuppliersPage'
import { CreateSupplierPage } from './pages/suppliers/CreateSupplierPage'
import { SupplierDetailPage } from './pages/suppliers/SupplierDetailPage'
import { NotFoundPage } from './pages/NotFoundPage'
import { GoogleReviewsPage } from './pages/reviews/GoogleReviewsPage'
import { SaleReversalsPage } from './pages/sales/SaleReversalsPage'

// Protected Route component
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated, isInitializing, checkAuth } = useAuthStore()
  
  useEffect(() => {
    checkAuth()
  }, [checkAuth])
  
  // Show loading while checking authentication
  if (isInitializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

function App() {
  const { initializeSocket } = useNotificationStore()
  
  useEffect(() => {
    initializeSocket()
  }, [initializeSocket])

  return (
    <Routes>
      {/* Auth Routes */}
      <Route element={<AuthLayout />}>
        <Route path="/login" element={<LoginPage />} />
      </Route>

      {/* Protected Routes */}
      <Route
        element={
          <ProtectedRoute>
            <MainLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<DashboardPage />} />
        
        {/* Products */}
        <Route path="/products" element={<ProductsPage />} />
        <Route path="/products/new" element={<CreateProductPage />} />
        <Route path="/products/:id" element={<ProductDetailPage />} />
        <Route path="/products/:id/edit" element={<CreateProductPage />} />
        <Route path="/stock-prices" element={<StockPricesPage />} />
        <Route path="/bundles" element={<BundlesPage />} />
        <Route path="/bundles/instances" element={<BundleInstancesPage />} />
        
        {/* Inventory */}
        <Route path="/inventory" element={<InventoryPage />} />
        <Route path="/inventory/adjust" element={<InventoryAdjustmentPage />} />
        
        {/* Suppliers */}
        <Route path="/suppliers" element={<SuppliersPage />} />
        <Route path="/suppliers/new" element={<CreateSupplierPage />} />
        <Route path="/suppliers/:id" element={<SupplierDetailPage />} />
        
        {/* Purchases */}
        <Route path="/purchases" element={<PurchasesPage />} />
        <Route path="/purchases/new" element={<CreatePurchasePage />} />
        <Route path="/purchases/:id" element={<PurchaseDetailPage />} />
        <Route path="/purchases/:id/receive" element={<ReceivePurchasePage />} />
        
        {/* Sales */}
        <Route path="/sales" element={<SalesPage />} />
        <Route path="/sales/:id" element={<SaleDetailPage />} />
        <Route path="/pos" element={<POSPage />} />
        <Route path="/sale-reversals" element={<SaleReversalsPage />} />
        
        {/* Quotations */}
        <Route path="/quotations" element={<QuotationsListPage />} />
        <Route path="/quotations/new" element={<CreateQuotationPage />} />
        
        {/* Customers */}
        <Route path="/customers" element={<CustomersPage />} />
        <Route path="/customers/new" element={<CreateCustomerPage />} />
        <Route path="/customers/:id" element={<CustomerDetailPage />} />
        <Route path="/customer-groups" element={<CustomerGroupsPage />} />
        
        {/* Consignments */}
        <Route path="/consignments" element={<ConsignmentsPage />} />
        <Route path="/consignments/new" element={<CreateConsignmentPage />} />
        <Route path="/consignments/:id" element={<ConsignmentDetailPage />} />
        
        {/* Financial */}
        <Route path="/payments" element={<PaymentsPage />} />
        <Route path="/invoices" element={<InvoicesPage />} />
        <Route path="/invoices/:id" element={<InvoiceDetailPage />} />
        
        {/* Reports & AI */}
        <Route path="/reports" element={<ReportsPage />} />
        <Route path="/reports/debtors" element={<DebtorsReportPage />} />
        <Route path="/reports/statements" element={<StatementsPage />} />
        <Route path="/ai-assistant" element={<AIAssistantPage />} />
        <Route path="/ai-reports" element={<AIReportsPage />} />
        
        {/* Reviews */}
        <Route path="/reviews/google" element={<GoogleReviewsPage />} />

        {/* System */}
        <Route path="/notifications" element={<NotificationsPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/brands" element={<BrandsPage />} />
        <Route path="/settings/categories" element={<CategoriesPage />} />
      </Route>

      {/* 404 */}
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  )
}

export default App
